import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, X, Copy, BookOpen, Sparkles, Flame, ChevronUp } from "lucide-react";
import api, { safeCopy } from "@/lib/api";

export default function SalesHUD({ earnings, openLeads, link, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [kpi, setKpi] = useState(null);

  const loadKpi = useCallback(() => {
    api.get("/sales/kpi/my?days=2").then((r) => setKpi(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadKpi();
    const t = setInterval(loadKpi, 60000);
    return () => clearInterval(t);
  }, [loadKpi]);

  const todayLog = (kpi?.logs || []).find((l) => l.date === kpi?.today) || {};
  const outreach = todayLog.outreach_dms || 0;
  const gold = kpi?.targets?.outreach_dms?.gold || 50;
  const outreachPct = Math.min(100, Math.round((outreach / gold) * 100));

  if (!open) {
    return (
      <button
        data-testid="sales-hud-open"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-[80] border border-nc-orange/50 bg-black/90 backdrop-blur-md px-4 py-2.5 flex items-center gap-3 hover:border-nc-orange transition-colors shadow-[0_0_24px_rgba(199,74,0,0.25)]"
      >
        <Zap className="w-4 h-4 text-nc-orange" />
        <span className="font-mono text-[10px] text-zinc-200">AUD {(earnings?.claimable || 0).toFixed(0)} claimable</span>
        <span className="font-mono text-[9px] text-zinc-500">{openLeads} leads</span>
        <ChevronUp className="w-3.5 h-3.5 text-zinc-500" />
      </button>
    );
  }

  return (
    <div data-testid="sales-hud" className="fixed bottom-4 right-4 z-[80] w-72 border border-nc-orange/50 bg-black/95 backdrop-blur-md shadow-[0_0_32px_rgba(199,74,0,0.3)]">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
        <span className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> Sales HUD</span>
        <button data-testid="sales-hud-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-3 text-center border-b border-white/10">
        <div className="px-2 py-3 border-r border-white/10">
          <p data-testid="hud-locked" className="font-sans font-bold text-sm text-zinc-100">{(earnings?.locked || 0).toFixed(0)}</p>
          <p className="font-mono text-[8px] text-zinc-500 uppercase">Locked</p>
        </div>
        <div className="px-2 py-3 border-r border-white/10">
          <p data-testid="hud-claimable" className="font-sans font-bold text-sm text-nc-green">{(earnings?.claimable || 0).toFixed(0)}</p>
          <p className="font-mono text-[8px] text-zinc-500 uppercase">Claimable</p>
        </div>
        <div className="px-2 py-3">
          <p data-testid="hud-open-leads" className="font-sans font-bold text-sm text-nc-orange">{openLeads}</p>
          <p className="font-mono text-[8px] text-zinc-500 uppercase">Open leads</p>
        </div>
      </div>
      <div className="px-4 py-3 border-b border-white/10">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono text-[9px] text-zinc-400 uppercase">Today's outreach</span>
          <span data-testid="hud-kpi-streak" className={`font-mono text-[9px] uppercase flex items-center gap-1 ${(kpi?.streak || 0) > 0 ? "text-nc-orange" : "text-zinc-600"}`}>
            <Flame className="w-3 h-3" /> {kpi?.streak || 0}d streak
          </span>
        </div>
        <div className="h-1.5 bg-white/5">
          <div data-testid="hud-kpi-bar" className={`h-1.5 transition-all duration-500 ${outreachPct >= 100 ? "bg-nc-orange" : "bg-nc-blue"}`} style={{ width: `${outreachPct}%` }} />
        </div>
        <p className="font-mono text-[8px] text-zinc-600 mt-1">{outreach}/{gold} DMs to gold tier</p>
      </div>
      <div className="grid grid-cols-3">
        <button data-testid="hud-copy-link" onClick={async () => { (await safeCopy(link)) ? toast.success("Personal link copied") : toast.error("Copy blocked"); }} className="px-2 py-3 font-mono text-[8px] tracking-[0.15em] uppercase text-zinc-300 hover:text-nc-orange border-r border-white/10 flex flex-col items-center gap-1.5 transition-colors">
          <Copy className="w-3.5 h-3.5" /> Copy link
        </button>
        <button data-testid="hud-open-guide" onClick={() => { onNavigate("guide"); setOpen(false); }} className="px-2 py-3 font-mono text-[8px] tracking-[0.15em] uppercase text-zinc-300 hover:text-nc-orange border-r border-white/10 flex flex-col items-center gap-1.5 transition-colors">
          <BookOpen className="w-3.5 h-3.5" /> Guide
        </button>
        <button data-testid="hud-open-tools" onClick={() => { onNavigate("tools"); setOpen(false); }} className="px-2 py-3 font-mono text-[8px] tracking-[0.15em] uppercase text-zinc-300 hover:text-nc-orange flex flex-col items-center gap-1.5 transition-colors">
          <Sparkles className="w-3.5 h-3.5" /> AI tools
        </button>
      </div>
    </div>
  );
}
